import {
  ChargingStation,
  ChargingStationPOI,
  ChargingStationsOverrideResponse,
  ChargingStationsResponse,
  Circuit,
  ConstructionSite,
  ConstructionSitePOI,
  ConstructionSitesResponse,
  WifiHotspotResponse,
} from "./APITypes";
import {parse as wktParse} from "wellknown";

export const extractWifiHotspots = (response: WifiHotspotResponse) => {
  //TODO
  return response.hotspots.map((hotspot, index) => ({
    original: hotspot,
    type: "wifi" as "wifi",
    id: `wifiHotspot${index}`,
    lat: 49.79 + Math.random() / 100,
    lng: 9.95 + Math.random() / 100,
    name: "Hotspot",
  }));
};

export const extractConstructionSites = (
  response: ConstructionSitesResponse,
) => {
  if (
    !response ||
    !Array.isArray(response.cats) ||
    response.cats.length === 0
  ) {
    console.info(
      'Fetched construction site data does not contain "cats" array or it is empty.',
    );
    return [];
  }

  const constructionCategory = response.cats.find(
    (cat) => cat.cn === "Baustellen",
  );

  if (!constructionCategory || !Array.isArray(constructionCategory.p)) {
    console.info(
      'No "Baustellen" category found or it does not contain "p" array.',
    );
    return [];
  }

  const constructionSitePOIs: ConstructionSitePOI[] =
    constructionCategory.p.map((item: ConstructionSite, index: number) => {
      // API 'll' field is "latitude,longitude" for points, WKT also present for lines/polygons
      const [latStr, lngStr] = item.ll ? item.ll.split(",") : ["0", "0"];
      let lat = parseFloat(latStr);
      let lng = parseFloat(lngStr);

      if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
        // console.warn(`Invalid or zero coordinates from 'll' for item: ${item.co || item.id}. Received: ${item.ll}. Defaulting to Würzburg center.`);
        lat = 49.7935;
        lng = 9.932;
      }

      let siteStartDate = item.attributes?.event?.start?.dateTime;
      let siteEndDate = item.attributes?.event?.end?.dateTime;
      if (siteStartDate)
        siteStartDate = new Date(siteStartDate).toLocaleDateString();
      if (siteEndDate) siteEndDate = new Date(siteEndDate).toLocaleDateString();

      const wktGeometry = wktParse(
        item.wkt?.trim().replaceAll("( ", "(").replaceAll(" )", ")") || "",
      );
      let apiStyle: ConstructionSitePOI["apiStyle"] | undefined = {
        color: "#ff0000",
        fillColor: "#ff0000",
        fillOpacity: 0.2,
        opacity: 1,
        weight: 4,
      };
      if (item.style?.geom) {
        apiStyle = item.style.geom;
      }

      let contactInfo =
        item.em || item.www
          ? {mail: item.em ?? undefined, website: item.www ?? undefined}
          : undefined;

      return {
        id: item.id || `constructionSite${index}`,
        type: "construction",
        name: item.co || "Baustelle",
        lat: lat,
        lng: lng,
        startDate: siteStartDate || "–",
        endDate: siteEndDate || "–",
        // startTime: 8, // Default operational hours
        // endTime: 17,  // Default operational hours
        details: item.desc || "–",
        wktGeometry: wktGeometry ?? undefined,
        apiStyle: apiStyle,
        media: item.media, // Store the media object
        contactInfo,
        original: item,
      };
    });

  return constructionSitePOIs;
};

export const extractChargingStations = (
  response: ChargingStationsResponse,
  overrides: ChargingStationsOverrideResponse,
) => {
  if (
    !response ||
    !Array.isArray(response.operator?.operatorPlaces) ||
    response.operator.operatorPlaces.length === 0
  ) {
    console.info(
      'Fetched charging stations data does not contain "operatorPlaces" array or it is empty.',
    );
    return [];
  }

  const locations: Record<
    string,
    ChargingStationPOI & {descriptions: string[]}
  > = {};

  response.operator.operatorPlaces.forEach(
    (item: ChargingStation, index: number) => {
      const cleanAddress = item.address.street
        .toLowerCase()
        .replaceAll("ß", "ss")
        .replaceAll("ä", "ae")
        .replaceAll("ü", "ue")
        .replaceAll("ö", "oe")
        .replaceAll(" ", "-")
        .replaceAll(/[^\-a-z0-9]/g, "");
      const extraInfo = overrides[cleanAddress] || {};
      if ((item.lat === 0 && item.lon === 0) || extraInfo.hide) return;
      const poi = {
        type: "charging" as const,
        id: `chargingStation${index}`,
        behindBarrier: extraInfo.behindBarrier ?? item.behindBarrier ?? false,
        lat: extraInfo.latitude ?? item.lat,
        lng: extraInfo.longitude ?? item.lon,
        name: extraInfo.name ?? item.name.replace(/(<([^>]+)>)/gi, ""), // remove html tags
        address:
          extraInfo.address ??
          `${item.address.street}, ${item.address.zipCode} ${item.address.city}`,
        description: (extraInfo.description ?? item.description) || "",
        descriptions: [(extraInfo.description ?? item.description) || ""],
        circuitCount: item.circuits.length,
        hours: extraInfo.hours ?? "24/7",
        imageLink: `/static/file_store/${extraInfo.image ?? cleanAddress + ".jpg"}`,
        priceLink:
          extraInfo.priceLink ??
          "https://www.wvv.de/elektromobilitaet/ladepreis/",
        original: item,
        circuits: item.circuits.map((circuit) => ({
          status: circuitToOccupancyStatus(circuit),
          powerType: circuit.power_type,
          plug: circuit.plug,
          maxPower:
            circuit.max_electric_power !== null
              ? Math.floor(circuit.max_electric_power / 1000) + "kW"
              : "–",
        })),
      };
      if (item.circuits.length === 0 || !item.published) return;
      if (locations[poi.address]) {
        locations[poi.address].circuits.push(...poi.circuits);
        locations[poi.address].circuitCount += poi.circuitCount;
        locations[poi.address].descriptions.push(poi.description);
      } else locations[poi.address] = poi;
    },
  );
  for (const location in locations) {
    locations[location].circuits.sort(
      (a, b) =>
        ["AC", "DC"].indexOf(a.powerType) * 3 +
        ["Frei", "Belegt", "Nicht verfügbar"].indexOf(a.status) -
        ["AC", "DC"].indexOf(b.powerType) * 3 +
        ["Frei", "Belegt", "Nicht verfügbar"].indexOf(b.status),
    );
    locations[location].description = locations[location].descriptions
      .filter((d, i, a) => a.indexOf(d) === i && d.trim().length > 0)
      .sort()
      .join(", ");
  }

  return Object.values(locations);
};

const availableChargingStationStatus = ["IDLE", "OCPP_AVAILABLE", "OK"];
const occupiedChargingStationStatus = [
  "CHARGING",
  "CHARGING_PAUSE",
  "FINISHED_CLEAN",
  "OCPP_OCCUPIED",
  "RUNNING",
  "FINISHED_DIRTY",
  "OCPP16_CHARGING",
  "STATE_CHARGING_PLUG_REMOVE",
];
const unavailableChargingStationStatus = ["ATTENTION", "CRITICAL"];

const circuitToOccupancyStatus: (
  circuit: Circuit,
) => "Status lädt…" | "Frei" | "Belegt" | "Nicht verfügbar" = (circuit) => {
  if (circuit.status === "LOADING") return "Status lädt…";
  if (availableChargingStationStatus.includes(circuit.status)) return "Frei";
  if (occupiedChargingStationStatus.includes(circuit.status)) return "Belegt";
  if (unavailableChargingStationStatus.includes(circuit.status))
    return "Nicht verfügbar";
  if (circuit.ready && !circuit.charging) return "Frei";
  if (circuit.charging) return "Belegt";
  return "Nicht verfügbar";
};
